import { useCallback, useEffect, useMemo, useState } from "react";
import { ActionIcon, Button, Flex, Group, Text } from "@mantine/core";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  archiveCampaign,
  editCampaign,
  getCampaignsStats,
} from "../../../../api/StatsAPI";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import MainTable from "../../../../layouts/Table/MainTable";
import CampaignColumnId from "./columns/CampaignColumnId";
import { CampaignColumnStatus } from "./columns/CampaignColumnStatus";
import { CampaignColumnName } from "./columns/CampaignColumnName";
import { CampaignLastSent } from "./columns/CampaignLastSent";
import { IconArchive, IconChevronRight, IconPlus } from "@tabler/icons-react";
import { CampaignColumnContacted } from "./columns/CampaignColumnContacted";
import { CampaignColumnRates } from "./columns/CampaignColumnRates";
import { CampaignColumnSender } from "./columns/CampaignColumnSender";
import { CampaignColumnDetails } from "./columns/CampaignColumnDetails";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import { useProfileStore } from "../../../../stores/UserStore";
import { Link, useParams } from "react-router-dom";
import CampaignActions from "./components/CampaignActions";
import NoItemsModal from "../../../../components/TableComponents/NoItemsModal";
import { sharingColumn } from "../../../../layouts/Table/columns/sharingColumn";
import { editProgrammation, editWorkflow } from "../../../../api/WorkflowAPI";
import SelectionSharer from "../../../../components/TableComponents/SelectionSharer";
import ExportCampaignTable from "./components/ExportCampaignTable";
import { CampaignStartingHour } from "./columns/CampaignStartingHour";
import { useCampaignStore } from "../../../../stores/CampaignStore";
import CampaignsTableFilter from "./CampaignsTableFilter";
import {
  transformServerToFilter,
  transformSortToServer,
} from "../../../../functions/utilFunctions";
import { tagsColumn } from "../../../../layouts/Table/columns/tagsColumn";

const CampaignsListing = ({ isExport }) => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const filterType = useCampaignStore((s) => s.filterType);
  const [tableFilter, setTableFilter] = useState(null);
  const [pageLock, setPageLock] = useState(false);
  const { filter } = useParams();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [query, setQuery] = useState(null);

  const columns = useMemo(
    () => [
      {
        field: "chevron",
        filterable: false,
        headerName: "",
        width: 24,
        renderCell: ({ row }) => {
          if (!row?.isChild)
            return (
              <ActionIcon
                // onClick={() => {
                //   apiRef.current.setRowChildrenExpansion(
                //     row.id,
                //     !apiRef.current.getRowNode(row.id).childrenExpanded
                //   );
                // }}
                p={0}
                disabled={row?.integrations?.length < 1}
                variant="subtle"
                color="primary"
                radius={"md"}
                size={"xl"}
                ml={-4}
                style={{
                  transform: apiRef.current.getRowNode(row.id).childrenExpanded
                    ? "rotate(90deg)"
                    : "none",
                }}
              >
                <IconChevronRight size={22} />
              </ActionIcon>
            );
          return "";
        },
      },
      dateColumn({
        field: "date_start",
        headerName: lang.statistics.columns.date_start,
        preferEmpty: true,
        withTimezone: true,
        sortable: false,
      }),
      CampaignColumnId(),
      CampaignColumnStatus({ apiRef }),
      CampaignColumnName(),
      CampaignColumnContacted(),
      CampaignColumnRates({
        field: "opens",
        headerName: lang.statistics.columns.rates.contacts_opened,
        identifier: "contacts_opened",
      }),
      CampaignColumnRates({
        field: "clicks",
        headerName: lang.statistics.columns.rates.contacts_clicked,
        identifier: "contacts_clicked",
      }),
      CampaignColumnRates({
        field: "answers",
        headerName: lang.statistics.columns.rates.contacts_answered,
        identifier: "contacts_answered",
      }),
      CampaignColumnDetails(),
      CampaignColumnSender(),
      tagsColumn({
        apiRef,
        editFunction:
          (row) =>
          ({ id, tags_ids }) =>
            editProgrammation(row.workflow_id, id, { tags_ids }),
        filterable: false,
      }),
      sharingColumn({
        field: "stats_sharings",
        apiRef,
        readOnly: true,
        sortable: false,
        editFunction:
          () =>
          ({ row, id, sharing }) =>
            editCampaign({
              programmation_id: id,
              workflow_id: row.workflow_id,
              data: { stats_sharings: sharing },
            }).then(() => {
              return editWorkflow({ id: row.workflow_id, sharing: sharing });
            }),
      }),
      CampaignStartingHour(),
      CampaignLastSent(),
      // dateColumn("stopped_on", lang.statistics.columns.paused_on, true),
      // dateColumn("archived_on", lang.statistics.columns.archived_on, true),
    ],
    [],
  );

  const actionsCell = useCallback(
    ({ row, value }) => {
      if (row?.isChild) {
        return "";
      }

      return (
        <Group wrap="nowrap" gap={8}>
          {/* <ActionIcon
            onClick={handleClick}
            p={0}
            disabled={row?.integrations?.length < 1}
            variant="filled"
            color="primary"
            radius={"md"}
          >
            <IconChevronDown size={22} />
          </ActionIcon> */}

          <CampaignActions
            apiRef={apiRef}
            current={row}
            onFinish={() => handleLoading({ isRefresh: true })}
            campaignFilter={filter}
          />
        </Group>
      );
    },
    [apiRef, filter],
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => {
      return (
        <>
          <SelectionDeleter
            handleDelete={({ parent_id, id }) =>
              archiveCampaign({ workflow_id: parent_id, programmation_id: id })
            }
            onFinish={() =>
              handleLoading({ isRefresh: false, toFirstPage: true })
            }
            ids={selectedIds}
            tooltip={lang.statistics.archive.label}
            icon={<IconArchive size={18} />}
            buttonTooltip={lang.campaigns.archive_title}
            warningText={
              <Text size="sm">
                {lang.campaigns.archive_warning} <br />
                <b>{lang.campaigns.archive_warning2}</b>
              </Text>
            }
            title={
              <Text size="md" truncate="end">
                {lang.campaigns.archive_title1}{" "}
                <b>
                  {" "}
                  {selectedIds.length} {lang.campaigns.archive_title2}{" "}
                </b>
              </Text>
            }
            submitButtonText={lang.statistics.archive.dialog.submit_button}
            getParentId={(id) => {
              return rows.find((x) => x.id === id)?.workflow_id;
            }}
          />

          <SelectionSharer
            getEditFunction={(row) =>
              ({ id, sharing }) =>
                editCampaign({
                  programmation_id: id,
                  workflow_id: row.workflow_id,
                  data: { stats_sharings: sharing },
                }).then(() => {
                  return editWorkflow({
                    id: row.workflow_id,
                    sharing: sharing,
                  });
                })
              }
            apiRef={apiRef}
            setTableLoading={() =>
              handleLoading({ isRefresh: true, toFirstPage: false })
            }
            ids={selectedIds}
            rows={rows}
          />

          <ExportCampaignTable />
        </>
      );
    },
    [apiRef, rows, lang, filter],
  );

  const handleLoading = useCallback(
    ({ isRefresh, toFirstPage }) => {
      if (loading) {
        return;
      }

      setLoading(true);

      if (toFirstPage) {
        setPageLock(true);
        apiRef.current.setPage(0);
      }

      const pagination = apiRef.current.state.pagination.paginationModel;
      const sortModel = apiRef.current.state.sorting.sortModel;
      // const tableQuery =
      //   apiRef.current.state.filter.filterModel.quickFilterValues?.join(" ");

      const filterType = useCampaignStore.getState().filterType;

      let quickFilterValues = [];

      let excludeValues = ["email", "linkedin", "sms", "smv"];

      if (filterType.length) {
        excludeValues.forEach((f) => {
          if (f === "linkedin") {
            quickFilterValues.push({
              mode: filterType.includes("linkedin") ? "or" : "and",
              values: [
                {
                  field_name: "contains_linkedin_message_step",
                  type: filterType.includes("linkedin")
                    ? "does_exist"
                    : "does_not_exist",
                  value: "",
                },
                {
                  field_name: "contains_linkedin_invitation_step",
                  type: filterType.includes("linkedin")
                    ? "does_exist"
                    : "does_not_exist",
                  value: "",
                },
              ],
            });
          } else {
            quickFilterValues.push({
              field_name: `contains_${f}_step`,
              type: filterType.includes(f) ? "does_exist" : "does_not_exist",
              value: "",
            });
          }
        });
      }

      let quickFilter = {
        mode: "and",
        values: quickFilterValues,
      };

      let finalFilter = {
        mode: "and",
        values: [quickFilter],
      };

      if (tableFilter) {
        finalFilter.values.push({
          mode: tableFilter.mode,
          values: tableFilter.values.map((valueItem) => {
            if (valueItem.field_name === "contains_step") {
              return {
                field_name: `contains_${valueItem.value}_step`,
                type: "does_exist",
                value: "",
              };
            }

            return valueItem;
          }),
        });
      }

      let tempFilter = filter;

      getCampaignsStats({
        type: filter,
        page: toFirstPage ? 1 : pagination.page + 1,
        query,
        filter: finalFilter,
        sort: transformSortToServer(sortModel),
      })
        .then((response) => {
          let results = response.data.programmations;

          let final = [];

          results.forEach((prog) => {
            let initial = prog.steps.find((x) => x.is_initial === true);

            const fromSharing =
              useProfileStore.getState().profile?.id !== prog?.created_by?.id;

            let parent = {
              ...prog,
              path: [prog.id.toString()],
              isParent: true,
              isChild: false,
              name: prog.workflow_name,
              initial,
              from_sharing: fromSharing,
              write_permission: !fromSharing,
            };

            let child = {
              path: [prog.id.toString(), "#" + prog.id.toString()],
              id: "#" + prog.id.toString(), // # = child row
              isChild: true,
              prog_id: prog.id,
              archived: prog.archived,
              prog_name: prog.workflow_name,
              stopped: prog.stopped,
              prog: prog,
              workflow: {
                name: prog.workflow_name,
                id: prog.workflow_id,
              },
              workflow_id: prog.workflow_id,
              steps: prog.steps,
              write_permission: !fromSharing,
            };

            final.push(parent);
            final.push(child);
          });

          const campaignStatus = location.pathname.split("/").pop();

          if (campaignStatus === tempFilter) {
            if (isRefresh) {
              apiRef.current.updateRows(final);
            } else {
              setRows(final);
            }
            setRowCount(response.data.number_results);
          }
        })
        .catch(() => {
          setLoading(false);
          setPageLock(false);
        })
        .finally(() => {
          setPageLock(false);
          setLoading(false);
        });
    },
    [apiRef, filter, tableFilter, query, loading],
  );

  const handlePagination = useCallback(
    (v, r) => {
      if (!pageLock)
        handleLoading({
          isRefresh: false,
          toFirstPage: false,
        });
    },
    [pageLock, handleLoading],
  );

  const handleSortChange = useCallback(
    (v, r) => {
      if (!pageLock) {
        handleLoading({
          isRefresh: false,
          toFirstPage: true,
        });
      }
    },
    [pageLock, handleLoading],
  );

  const handleSearch = useCallback((searchValue = "") => {
    setQuery(searchValue);
  }, []);

  useEffect(() => {
    handleLoading({ toFirstPage: true });
  }, [filterType, tableFilter, filter, query]);

  return (
    <Flex flex={"1 1 auto"} pos={"relative"} h={0}>
      <NoItemsModal
        rows={rows}
        loading={loading}
        text={
          <Text size="xl" mb={32}>
            {lang.campaigns.no_campaign_found}
          </Text>
        }
        button={
          filter === "running" ? (
            <Button
              leftSection={<IconPlus size={18} />}
              radius={"md"}
              color="primary"
              component={Link}
              to={"/campaign/create"}
              size="md"
            >
              {lang.campaigns.create_campaign}
            </Button>
          ) : (
            <Flex h={100} />
          )
        }
      />

      <MainTable
        key={filter}
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        loading={loading}
        setLoading={(v) =>
          handleLoading({
            isRefresh: true,
            toFirstPage: false,
          })
        }
        id={"list-table"}
        actionsCell={actionsCell}
        toolbarActions={
          apiRef?.current?.state?.filter?.filterModel?.items?.[0]?.value !==
          "archived"
            ? toolbarActions
            : undefined
        }
        treeData
        pageOrderName={"stats_order"}
        onRowClick={(params) => {
          if (typeof params.id === "string") {
            return;
          }

          apiRef.current.setRowChildrenExpansion(
            params.id,
            !apiRef.current.getRowNode(params.id).childrenExpanded,
          );
        }}
        withBorderTop
        hidePerPage
        pinnedLeft={["chevron", GRID_CHECKBOX_SELECTION_FIELD, "id", "stopped"]}
        pinnedRight={["actions", GRID_TREE_DATA_GROUPING_FIELD]}
        onPaginationChange={handlePagination}
        onSortChange={handleSortChange}
        onSearchChange={handleSearch}
        disablePerPage
        defaultPerPage={25}
        isClient={false}
        actionsCellWidth={75}
        disableFilter
        whiteButtons
        filterModel={transformServerToFilter(tableFilter, query)}
        customFilterComponent={
          <CampaignsTableFilter
            filter={tableFilter}
            setFilter={setTableFilter}
          />
        }
        tableSx={{
          "& .table-toolbar": {
            display: isExport ? "none" : "flex",
          },
          "& .table-search": {
            marginLeft: "672px",
            width: "180px !important",
          },
          "& .table-row.Mui-hovered": {
            background: "var(--mantine-color-primary-2) !important",
            color: "black !important",

            "& a": {
              color: "black !important",
              textDecoration: "none !important",
            },
          },
          "& .goto-page": {
            marginRight: 2
          }
        }}
      />
    </Flex>
  );
};

export default CampaignsListing;
